import {BookingPracticeAppointmentReasonFieldsFragment} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {useCurrentFlowId, useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {BookingStep} from "@page-containers/booking/utils/bookingStep";
import {useTranslation} from "@i18n/client";
import React, {useEffect, useState} from "react";
import {v4} from "uuid";

import {MonolithLocationFields} from "../../../../_services/graphql/queries/location";
import {I18nNamespace} from "@i18n";
import {CustomCategory, EventType} from "../../../../utils/analytics/constants";
import {queueEvent} from "../../../../utils/analytics/eventQueue";
import Heading from "../Heading";
import {useFamilyMembersForAuthedPatientQuery} from "../PatientSelection/useFamilyMembersQuery";
import CompleteCheckInButton from "./CompleteCheckInButton";
import ConfirmationTile from "./ConfirmationTile";
import FollowUpButtons from "./FollowUpButtons";
import useIsSaasLocation from "../../../../hooks/useIsSaasLocation";

type Props = {
  location?: MonolithLocationFields;
  selectedTime?: number;
  practiceAppointmentReason?: BookingPracticeAppointmentReasonFieldsFragment;
  appointmentId?: string;
  specialtyId: string;
};

const CompleteCheckIn: React.FC<Props> = ({
  location,
  selectedTime,
  practiceAppointmentReason,
  appointmentId,
  specialtyId,
}) => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const flowId = useCurrentFlowId();
  const [viewId] = useState(v4());
  const {doubleBookingOf, rescheduling, selectedPatientId} = useParsedBookingQuery();
  const {familyMembers} = useFamilyMembersForAuthedPatientQuery();

  const patientFirstName = familyMembers?.find(member => member.id === selectedPatientId)
    ?.firstName;

  useEffect(() => {
    if (flowId) {
      queueEvent({
        typeId: EventType.ApptConfirmationStepViewed,
        extraData: {
          viewId,
          category: CustomCategory.AppointmentScheduling,
          bookingStep: BookingStep.APPOINTMENT_CONFIRMATION,
          flowId,
        },
      });
    }
  }, [flowId, viewId]);

  const text = useHeaderText(doubleBookingOf, rescheduling ?? false, patientFirstName);

  const isSaasLocation = useIsSaasLocation(location);

  return (
    <div>
      <div className="flex flex-col items-center">
        <Heading>{text}</Heading>
        <div className="mt-4 body-md tac max-w-[16rem] sm:max-w-none">
          {i18n.t("Save 15 minutes at the office by checking in now", {
            ns: "web-scheduling",
          })}
        </div>
        <CompleteCheckInButton appointmentId={appointmentId} practiceId={location?.practiceId} />
      </div>
      <ConfirmationTile
        location={location}
        practiceAppointmentReason={practiceAppointmentReason}
        selectedTime={selectedTime}
        specialtyId={specialtyId}
      />
      <FollowUpButtons location={location} viewId={viewId} appointmentId={appointmentId} />
      {!isSaasLocation && (
        <div className="text-center mt-4">
          <em className="text-sm text-gray-700 leading-5 font-ir not-italic">
            {i18n.t(
              "There is a $50 fee for no-shows or cancellations with less than 24 hours notice.",
              {ns: "web-scheduling"},
            )}
          </em>
        </div>
      )}
    </div>
  );
};

function useHeaderText(
  doubleBookingOf: string | null,
  rescheduling: boolean,
  patientName?: string,
) {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  if (rescheduling) {
    if (patientName) {
      return i18n.t("{{patientName}}'s visit is rescheduled", {
        patientName,
        ns: "web-scheduling",
      });
    } else {
      return i18n.t("Your visit is rescheduled", {
        ns: "web-scheduling",
      });
    }
  }
  if (doubleBookingOf) {
    if (patientName) {
      return i18n.t("{{patientName}}'s original visit is confirmed", {
        patientName,
        ns: "web-scheduling",
      });
    } else {
      return i18n.t("Your original visit is confirmed", {
        ns: "web-scheduling",
      });
    }
  }
  if (patientName) {
    return i18n.t("{{patientName}}'s visit is booked", {
      ns: "web-scheduling",
      patientName,
    });
  } else {
    return i18n.t("Your visit is booked", {ns: "web-scheduling"});
  }
}

export default CompleteCheckIn;
